import cn from "classnames";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { projectFiltersSelector } from "redux/modules/common/building/project/selectors";

import PopoverOverlay from "components/UI/_TODO/PopoverOverlay/PopoverOverlay";
import { PopoverOverlayWithRealigning } from "components/UI/_TODO/PopoverOverlay/PopoverOverlayWithRealigning";

import { useBuildingPermissionsContext } from "../../contexts/BuildingPermissionsContext";
import ExpenditureAllIntervalsModal from "../ExpenditureAllIntervalsModal/ExpenditureAllIntervalsModal";
import { useTypedSelector } from "app/store/typedUseSelector";
import { getAllIntervals } from "entities/Intervals/model/thunks";

import { BuildingIntervalsByCategory } from "../../types";
import { IBaseInterval } from "entities/Intervals/model/types";
import { IExpenditure } from "types/interfaces/Expenditure";

import { daysPluralSuffix } from "utils/formatters/daysPluralSuffix";
import { generateStorageKey } from "utils/helpers/generateStorageKey";

import { ReactComponent as CalendarBlack } from "images/icons/navigation/calendar-black.svg";

import styles from "./BuildingAllIntervalsPopup.module.scss";

interface IProps {
  intervals: BuildingIntervalsByCategory;
  buildingId: number;
  expenditure: IExpenditure;
  sectionName: string | undefined;
  sectionId: number;
  canDeleteFiles: boolean;
  loadProduct: () => void;
  constructionDateStart: string;
  constructionDateEnd: string;
  className?: string;
}

const BuildingAllIntervalsPopup = ({
  buildingId,
  intervals,
  expenditure,
  sectionName,
  sectionId,
  loadProduct,
  canDeleteFiles,
  constructionDateStart,
  constructionDateEnd,
  className,
}: IProps) => {
  const { canViewPlan, canViewProgress } = useBuildingPermissionsContext();
  const dispatch = useDispatch();
  const filters = useSelector(projectFiltersSelector);
  const projectStructure = filters.attribute ? "segments" : "sections";
  const [isModalOpen, setIsModalOpened] = useState(false);
  const openModal = () => {
    setIsModalOpened(true);
  };
  const closeModal = () => {
    setIsModalOpened(false);
  };
  const key = generateStorageKey({
    startAt: constructionDateStart,
    endAt: constructionDateEnd,
    sectionId: sectionId,
    type: projectStructure,
    buildingId: buildingId,
  });
  const intervalsData = useTypedSelector((state) => state.intervalsReducer.intervals)[key];

  const { intervalsList, plans, facts } = useMemo(() => {
    const plans = (canViewPlan ? intervalsData?.plans ?? [] : [])
      .filter((el) => el.expenditure_id === expenditure.id)
      .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
    const facts = (
      canViewProgress
        ? [...(intervalsData?.facts ?? []), ...(intervalsData?.donned ?? []), ...(intervalsData?.received ?? [])]
        : []
    )
      .filter((el) => el.expenditure_id === expenditure.id)
      .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
    return {
      intervalsList: [...plans, ...facts].sort(
        (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
      ),
      plans,
      facts,
    };
  }, [intervals, intervalsData, canViewPlan, canViewProgress, expenditure.id]);

  if (!intervalsList.length) return <div />;

  return (
    <>
      <PopoverOverlayWithRealigning
        openType="hover"
        placement="left-end"
        popoverBorderColor="primary"
        calculatePlacement={(offsetFromBottom) => {
          if (intervalsList?.length <= 1) {
            return "left";
          }
          if (offsetFromBottom > 200) {
            return "left";
          }
          return "left-end";
        }}
        content={
          <div className={styles.content} onClick={openModal}>
            <div className={styles.title}>Производство</div>
            <div className={styles.list}>
              {intervalsList.map((i: any) => {
                const diff = moment(i.end_at).diff(moment(i.start_at), "d") + 1;
                return (
                  <div className={styles.row} key={i.interval_id}>
                    <span>{`${moment(i.start_at).format("DD/MM/YYYY")} - ${moment(i.end_at).format(
                      "DD/MM/YYYY"
                    )}`}</span>
                    <span className={styles.duration}>{`Продолжительность – ${diff} ${daysPluralSuffix(diff)}`}</span>
                  </div>
                );
              })}
            </div>
          </div>
        }
      >
        <div className={cn(styles.mark, className)} onClick={openModal}>
          <CalendarBlack />
        </div>
      </PopoverOverlayWithRealigning>
      <ExpenditureAllIntervalsModal
        buildingId={buildingId}
        isOpen={isModalOpen}
        onClose={closeModal}
        expenditure={expenditure}
        sectionName={sectionName}
        sectionId={sectionId}
        loadProduct={loadProduct}
        date_end={constructionDateEnd}
        date_start={constructionDateStart}
        canDeleteFiles={canDeleteFiles}
        defaultTab={!plans.length ? "fact" : "plan"}
      />
    </>
  );
};

export default BuildingAllIntervalsPopup;
